import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { useUpsertCoveredParticipant } from 'components/hooks/covered_participants/useUpsertCoveredParticipant'
import { Banner } from 'components/reusable/Banner'
import { FormCheckboxFieldDS, FormDateFieldDS, FormSelectFieldDS, FormTextFieldDS } from 'components/reusable/form/FormFields'
import { Button } from 'slab'
import { formatSSN } from 'utils'
import { type BrokersCoveredParticipant } from 'utils/web_api.schemas'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet'

type CoveredParticipant = Partial<BrokersCoveredParticipant>

interface CoveredParticipantSheetProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  memberId: string
  planYear: number
  participant?: CoveredParticipant
}

const defaultValues: CoveredParticipant = {
  family_name: '',
  given_name: '',
  gender: '',
  relationship: '',
  ssn: '',
  dob: '',
  tobacco: false,
  status: 'pending',
}

function getParticipantFormValues (participant: CoveredParticipant | undefined) {
  if (!participant) return defaultValues
  return {
    ...participant,
    ssn: participant.ssn ? formatSSN(participant.ssn) : '',
    status: participant.status === 'pending' ? 'pending' : 'active'
  }
}

const CoveredParticipantSheet = ({
  isOpen,
  setIsOpen,
  memberId,
  planYear,
  participant,
}: CoveredParticipantSheetProps) => {
  const isEditing = !!participant
  const isSelf = participant?.relationship === 'self'

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CoveredParticipant>({
    defaultValues,
    values: {
      ...getParticipantFormValues(participant)
    }
  })

  const saveParticipantData = useUpsertCoveredParticipant({
    memberId,
    planYear,
    participantId: participant?.id,
    onSuccess: () => {
      toast.success('Participant data saved successfully')
      setIsOpen(false)
      reset()
    },
    onError: (error: unknown) => {
      const errorMessage = (error as any)?.message || 'An error occurred'
      toast.error(errorMessage)
    },
  })

  const onSubmit = (data: CoveredParticipant) => {
    // When submitting the form, convert "active" status to null so the backend treats it as "active" per our convention.
    const submitData = {
      ...data,
      status: data.status === 'active' ? null : data.status
    } as CoveredParticipant
    saveParticipantData.mutate(submitData)
  }

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (isOpen) {
        const activeElement = document.activeElement
        const sheetElement = document.querySelector('[data-sheet="create-covered-participant"]')
        const focusWithinSheet = sheetElement && sheetElement.contains(activeElement)

        // If focus is within the sheet, prevent the Drawer's custom keyboard handling.
        if (focusWithinSheet) {
        // For Tab key, we want normal browser behavior (tabbing between fields).
          if (event.code === 'Tab') {
            event.stopImmediatePropagation() // Prevents other handlers from running.
            return
          }

          // For Space key, we also want normal behavior (e.g., clicking buttons)
          if (event.code === 'Space') {
            event.stopImmediatePropagation()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress, { capture: true })

    return () => {
      document.removeEventListener('keydown', handleKeyPress, { capture: true })
    }
  }, [isOpen])

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className='h-dvh overflow-y-auto' style={{ minWidth: '720px' }} data-sheet='create-covered-participant'>
        <div className='mb-4'>
          <Banner titleText={`You are ${isEditing ? 'editing' : 'adding'} a participant, please be aware that this will not update Carrier or Unit records.`} type='caution' />
        </div>
        <SheetHeader>
          <SheetTitle className='heading-lg'>
            {isEditing ? 'Edit Dependent' : 'Add Dependent'}
          </SheetTitle>
        </SheetHeader>
        <SheetDescription className='mb-4'>
          {isEditing ? 'Edit the dependent details below.' : 'Enter the dependent details below.'}
        </SheetDescription>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <FormTextFieldDS
              fieldId='given_name'
              fieldLabel='Given Name'
              placeholder='Enter Given Name'
              register={register}
              validationRule={{ required: 'Given name is required' }}
              error={errors.given_name}
            />

            <FormTextFieldDS
              fieldId='family_name'
              fieldLabel='Family Name'
              placeholder='Enter Family Name'
              register={register}
              validationRule={{ required: 'Family name is required' }}
              error={errors.family_name}
            />

            <FormSelectFieldDS
              fieldId='gender'
              fieldLabel='Gender'
              placeholder='Select Gender'
              fieldOptions={[
                { label: 'Male', value: 'male' },
                { label: 'Female', value: 'female' },
                { label: 'Non-binary', value: 'non_binary' },
              ]}
              register={register}
              validationRule={{ required: 'Gender is required' }}
              error={errors.gender}
            />

            <FormSelectFieldDS
              fieldId='relationship'
              fieldLabel='Relationship'
              placeholder='Select Relationship'
              fieldOptions={[
                { label: 'Self', value: 'self', disabled: !isSelf },
                { label: 'Spouse', value: 'spouse', disabled: isSelf },
                { label: 'Child', value: 'child', disabled: isSelf },
              ]}
              register={register}
              validationRule={{ required: 'Relationship is required' }}
              error={errors.relationship}
            />

            <FormTextFieldDS
              fieldId='ssn'
              fieldLabel='SSN (optional)'
              placeholder='Enter SSN'
              register={register}
              validationRule={{
                pattern: { value: /^\d{3}-?\d{2}-?\d{4}$/, message: 'Please enter a valid SSN (XXX-XX-XXXX)' },
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  e.target.value = formatSSN(e.target.value)
                }
              }}
              error={errors.ssn}
            />

            <FormDateFieldDS
              fieldId='dob'
              fieldLabel='Date of Birth'
              register={register}
              validationRule={{ required: 'Date of birth is required' }}
              error={errors.dob}
            />

            <FormSelectFieldDS
              fieldId='status'
              fieldLabel='Status'
              placeholder='Select Status'
              fieldOptions={[
                { label: 'Active', value: 'active' },
                { label: 'Pending', value: 'pending' },
              ]}
              register={register}
              validationRule={{ required: 'Status is required' }}
              error={errors.status}
            />
          </div>

          <FormCheckboxFieldDS
            fieldId='tobacco'
            fieldLabel='Tobacco User'
            register={register}
            error={errors.tobacco}
          />
          <div className='flex justify-end gap-3 pt-4'>
            <Button
              variant='secondary'
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>

            <Button
              data-testid={isEditing ? 'update-dependent-button' : 'add-dependent-button'}
              type='submit'
              variant='primary'
            >
              {isEditing ? 'Update' : 'Add'} Dependent
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export default CoveredParticipantSheet
